import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import {
	Button,
	Col,
	Container,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import indicadoresService from "services/IndicadoresService";
import permissionsService from "services/permissionsService";
import IndicadoresConfigurationService from "services/IndicadoresConfigurationService";
import userService from "services/UserService";
import OrganizationalStructureService from "services/OrganizationalStructureService";
import withRouter from "components/Common/withRouter";
import Configuration from "./components/Configuration";
import VariableConfiguration from "./components/VariableConfiguration";
import Loading from "components/Common/Loading";
import CopyIndicadorModal from "./components/CopyIndicadorModal";
import { Copy, FloppyDisk, SignOut } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import { generateYearOptions } from "helpers/yearHelper";

export const customStyles = {
	option: (provided, state) => ({
		...provided,
		color: state.isSelected ? "blue" : "black",
		backgroundColor: "white",
		"&:hover": {
			backgroundColor: "#556ee6",
		},
	}),
	menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const selectPortalProps = {
	menuPortalTarget: document.body,
	menuPosition: "fixed",
};

const yearOptions = generateYearOptions();

const statusOptions = [
	{ value: "Ativo", label: "Ativo" },
	{ value: "Inativo", label: "Inativo" },
];

const periodicityOptions = [
	{ value: "Mensal", label: "Mensal" },
	{ value: "Bimestral", label: "Bimestral" },
	{ value: "Trimestral", label: "Trimestral" },
	{ value: "Quadrimestral", label: "Quadrimestral" },
	{ value: "Semestral", label: "Semestral" },
	{ value: "Anual", label: "Anual" },
];

export const IndicadorFormContent = ({
	id: initialId,
	onClose,
	onSaved,
	isModal = false,
	t,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const variableConfigRef = useRef(null);

	const [currentId, setCurrentId] = useState(initialId);
	const id = currentId;
	const mountIdRef = useRef(initialId);

	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loading, setLoading] = useState(false);

	const [usuarios, setUsuarios] = useState([]);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [organizacoes, setOrganizacoes] = useState([]);
	const [temPermissaoCopiar, setTemPermissaoCopiar] = useState(false);
	const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
	const [loggedUserId, setLoggedUserId] = useState(null);

	const getBackLink = () => {
		const filters = location.state?.filters;
		if (filters) {
			return { pathname: "/indicadores", state: { filters } };
		}
		return "/indicadores";
	};

	const validationSchema = Yup.object().shape({
		intention_goals: Yup.string().required("Campo obrigatório"),
		responsible_id: Yup.string().nullable().required("Campo obrigatório"),
		organization_id: Yup.string().nullable().required("Campo obrigatório"),
		collaborators: Yup.array()
			.min(1, "Campo obrigatório")
			.required("Campo obrigatório"),
		additional_informations: Yup.string().nullable(),
		notify_involved: Yup.string(),
	});

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			id: data?.id || null,
			intention_goals: data?.intention_goals || "",
			organization_id: data?.organization_id || null,
			responsible_id: data?.responsible_id || null,
			additional_informations: data?.additional_informations || "",
			notify_involved: data?.notify_involved || "0",
			collaborators: data?.collaborators || [],
			year: data?.year || new Date().getFullYear(),
			periodicity: data?.periodicity || "Mensal",
			status: data?.status || "Ativo",
		},
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setLoading(true);
				const preparedValues = {
					...values,
					collaborators: Array.isArray(values.collaborators)
						? values.collaborators.every(
								(item) => typeof item === "object",
						  )
							? values.collaborators.map((item) => item.value)
							: values.collaborators
						: [],
				};

				// O modal é o fluxo novo (estrutura de variáveis).
				// A página antiga (isModal=false) mantém indicadores "legacy".
				if (isModal) {
					preparedValues.structure_version = "variables";
				}

				if (id) {
					await indicadoresService.put(id, preparedValues);
					// Se houver remoções pendentes vindas do efeito de periodicidade,
					// aplica agora somente quando o usuário salvar.
					if (isModal) {
						await variableConfigRef.current?.savePendingConfigs?.();
					}
					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Formulário salvo com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
					onSaved?.();
					// Mesmo comportamento da criação: fecha o modal ao salvar.
					if (isModal) {
						onClose?.();
					}
			} else {
				const resp = await indicadoresService.post(preparedValues);
				if (resp?.data?.id) {
					if (isModal) {
						if (variableConfigRef.current?.savePendingConfigs) {
							await variableConfigRef.current.savePendingConfigs(resp.data.id);
						}
						setCurrentId(resp.data.id);
						SendaCustomSwal.fire({
							iconHtml: '<i class="success-icon"></i>',
							title: "Sucesso",
							text: "Indicador criado com sucesso!",
							customClass: {
								confirmButton: "custom-confirm-button",
							},
						});
						onSaved?.();
						// Após criar (fluxo novo) podemos fechar o modal.
						onClose?.();
					} else {
						navigate(`/indicadores/form/${resp?.data?.id}`);
					}
				}
			}
			} catch (error) {
				SendaCustomSwal.fire({
					iconHtml: '<i class="error-icon"></i>',
					title: "Oops...",
					text: "Algo deu errado!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} finally {
				setLoading(false);
			}
			setSubmitting(false);
		},
	});

	async function getOrganizacoes() {
		try {
			const response = await OrganizationalStructureService.get();
			const organizacoes = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));
			setOrganizacoes(organizacoes);
		} catch (error) {
			console.log(error);
		}
	}

	async function getUsuarios() {
		try {
			const response = await userService.getAllCompany();
			const funcoes = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	}

	const handleChange = (selectedOption) => {
		setSelectedOptions(selectedOption);
		const selectedValues = selectedOption
			? selectedOption.map((option) => option.value)
			: [];
		validation.setFieldValue("collaborators", selectedValues);
	};

	const getItems = async (itemId) => {
		const fetchId = itemId || id;
		try {
			const response = await indicadoresService.getById(fetchId);
			// O backend atual pode não retornar `periodicity` (campo não existe no model de Goals).
			// Para não resetar o select do modal (e nem disparar regras de periodicidade),
			// reaproveitamos o valor atual do form quando vier vazio.
			if (
				(response.periodicity === undefined || response.periodicity === null || response.periodicity === "") &&
				validation?.values?.periodicity
			) {
				response.periodicity = validation.values.periodicity;
			}
			response.collaborators = response.collaborators.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			setData(response);
			setSelectedOptions(response.collaborators);
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissions = async () => {
		try {
			const userPermission = await permissionsService.getUserPermission();
			setLoggedUserId(userPermission.user_id);

			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "goalCopy") {
					setTemPermissaoCopiar(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getAll = async () => {
		setIsLoading(true);
		if (id) {
			await getItems();
		}
		await getUsuarios();
		await getOrganizacoes();
		await getPermissions();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, []);

	useEffect(() => {
		if (currentId && currentId !== mountIdRef.current) {
			getItems(currentId);
		}
	}, [currentId]);

	const handleCheckboxChange = () => {
		const currentValue = validation.values.notify_involved;
		const newValue = currentValue === "1" ? "0" : "1";
		validation.setFieldValue("notify_involved", newValue);
	};

	const handleCopyIndicador = () => {
		setIsCopyModalOpen(true);
	};

	const handleConfirmCopy = async (copyData) => {
		try {
			setIsLoading(true);

			const isNewStructure =
				(data?.structure_version || "legacy") === "variables";

			let isDestV2Enabled = false;
			if (isNewStructure) {
				const destV2Response = await indicadoresService.getV2Enabled(
					copyData.company_id,
				);
				isDestV2Enabled = !!destV2Response?.enabled;
			}

			const newGoalCopyData = {
				intention_goals: data?.intention_goals,
				organization_id: copyData.department_id,
				responsible_id: loggedUserId,
				additional_informations: data?.additional_informations,
				notify_involved: data?.notify_involved,
				year: data?.year,
				status: data?.status,
				...(isNewStructure && isDestV2Enabled
					? { structure_version: "variables" }
					: {}),
			};

			const newGoalResponse = await indicadoresService.postCopyGoal(
				newGoalCopyData,
				copyData.company_id,
			);
			const newGoal = newGoalResponse.data;

			if (isNewStructure && isDestV2Enabled) {
				// Fluxo novo: copia goals_variables_config (e o catálogo de
				// variáveis é resolvido no schema de destino pelo backend).
				const sourceConfigs =
					await indicadoresService.getVariablesConfig(id);

				const configsPayload = (sourceConfigs || []).map((config) => ({
					id: config.id,
					variable:
						config.goals_variables_id && config.GoalsVariable
							? {
									name: config.GoalsVariable.name,
									quantifier: config.GoalsVariable.quantifier,
									periodicity: config.GoalsVariable.periodicity,
									decimals_cases:
										config.GoalsVariable.decimals_cases,
							  }
							: null,
					name: config.name,
					quantifier: config.quantifier,
					type: config.type,
					formula: config.formula,
					display_on_chart: config.display_on_chart,
					color: config.color,
					sequence: config.sequence,
					type_meta_definition: config.type_meta_definition,
					totalizator: config.totalizator || null,
					totalizator_ref: config.totalizator_ref || null,
				}));

				if (configsPayload.length > 0) {
					await indicadoresService.postCopyVariableConfigs(
						copyData.company_id,
						{
							goals_id: newGoal.id,
							configs: configsPayload,
						},
					);
				}
			} else if (data?.goals_config && data.goals_config.length > 0) {
				// Fluxo antigo: copia goals_config em lote. O `id` original é
				// enviado apenas para o backend remapear o totalizator_ref
				// para o id equivalente na cópia (via column); não é
				// persistido como id da nova coluna.
				const configsPayload = data.goals_config.map((config) => ({
					id: config.id,
					name: config.name,
					type: config.type,
					quantifier: config.quantifier,
					totalizator: config.totalizator,
					sequence: config.sequence,
					column: config.column,
					display_on_chart: config.display_on_chart,
					color: config.color,
					formula: config.formula,
					only_read: config.only_read,
					show_on_second_axis: config.show_on_second_axis,
					totalizator_ref: config.totalizator_ref,
					decimals_cases: config.decimals_cases,
					type_meta_definition: config.type_meta_definition,
				}));

				await IndicadoresConfigurationService.postCopyGoalConfigsBatch(
					copyData.company_id,
					{
						goals_id: newGoal.id,
						configs: configsPayload,
					},
				);
			}

			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Indicador copiado com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			console.error("Erro ao copiar indicador:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: "Não foi possível copiar o indicador.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	const buttonStyle = {
		width: 150,
		background: "none",
		padding: 10,
		cursor: "pointer",
		color: "black",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 2,
	};

	const formJSX = (
		<Form
			className="needs-validation"
			onSubmit={(e) => {
				e.preventDefault();
				validation.handleSubmit();
				return false;
			}}
		>
			<Row>
				<Col md="4">
					<FormGroup className="mb-3">
						<Label>
							Intenção do indicador
							<span className="text-danger">*</span>
						</Label>
						<Input
							name="intention_goals"
							placeholder="Intenção do indicador"
							type="text"
							className="form-control"
							id="validationCustom01"
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.intention_goals || ""}
							invalid={
								validation.touched.intention_goals &&
								!!validation.errors.intention_goals
							}
						/>
						{validation.touched.intention_goals &&
						validation.errors.intention_goals ? (
							<FormFeedback type="invalid">
								{validation.errors.intention_goals}
							</FormFeedback>
						) : null}
					</FormGroup>
				</Col>
				<Col md="4">
					<FormGroup className="mb-3">
						<Label>
							Responsável
							<span className="text-danger">*</span>
						</Label>
						<Select
							{...selectPortalProps}
							styles={customStyles}
							placeholder="Selecione o Responsável"
							onChange={(selectedOption) => {
								const selectedValue = selectedOption
									? selectedOption.value
									: null;
								validation.setFieldValue(
									"responsible_id",
									selectedValue,
								);
							}}
							value={usuarios?.find(
								(option) =>
									option.value ===
									validation.values.responsible_id,
							)}
							options={usuarios}
							className={
								validation.touched.responsible_id &&
								validation.errors.responsible_id
									? "is-invalid"
									: ""
							}
						/>
						{validation.touched.responsible_id &&
						validation.errors.responsible_id ? (
							<FormFeedback type="invalid">
								{validation.errors.responsible_id}
							</FormFeedback>
						) : null}
					</FormGroup>
				</Col>
				<Col md="4">
					<FormGroup className="mb-3">
						<Label>
							Departamento
							<span className="text-danger">*</span>
						</Label>
						<Select
							{...selectPortalProps}
							styles={customStyles}
							placeholder="Selecione o departamento"
							onChange={(selectedOption) => {
								const selectedValue = selectedOption
									? selectedOption.value
									: null;
								validation.setFieldValue(
									"organization_id",
									selectedValue,
								);
							}}
							value={organizacoes?.find(
								(option) =>
									option.value ===
									validation.values.organization_id,
							)}
							options={organizacoes}
							classNamePrefix={
								validation.touched.organization_id &&
								validation.errors.organization_id
									? "react-select__is-invalid"
									: ""
							}
						/>
						{validation.touched.organization_id &&
						validation.errors.organization_id ? (
							<FormFeedback type="invalid">
								{validation.errors.organization_id}
							</FormFeedback>
						) : null}
					</FormGroup>
				</Col>
				<Col md="12">
					<FormGroup className="mb-3">
						<Label>
							Quem pode ver
							<span className="text-danger">*</span>
						</Label>
						<Select
							{...selectPortalProps}
							isMulti={true}
							styles={customStyles}
							placeholder="Selecione quem pode ver"
							onChange={(selectedOption) => {
								handleChange(selectedOption);
							}}
							options={usuarios}
							value={selectedOptions}
							className={
								validation.touched.collaborators &&
								validation.errors.collaborators
									? "is-invalid"
									: ""
							}
						/>
						{validation.touched.collaborators &&
						validation.errors.collaborators ? (
							<FormFeedback type="invalid">
								{validation.errors.collaborators}
							</FormFeedback>
						) : null}
					</FormGroup>
				</Col>
		{isModal && (
			<>
				<Col md="4">
					<FormGroup className="mb-3">
						<Label>
							Ano de Referência
							<span className="text-danger">*</span>
						</Label>
						<Input
							type="select"
							value={validation.values.year || new Date().getFullYear()}
							onChange={(e) => validation.setFieldValue("year", e.target.value)}
						>
							{yearOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Input>
					</FormGroup>
				</Col>
				<Col md="4">
					<FormGroup className="mb-3">
						<Label>
							Periodicidade
							<span className="text-danger">*</span>
						</Label>
						<Select
							{...selectPortalProps}
							styles={customStyles}
							value={periodicityOptions.find((o) => o.value === validation.values.periodicity) || periodicityOptions[0]}
							onChange={(opt) => validation.setFieldValue("periodicity", opt.value)}
							options={periodicityOptions}
						/>
					</FormGroup>
				</Col>
				<Col md="4">
					<FormGroup className="mb-3">
						<Label>Status</Label>
						<Select
							{...selectPortalProps}
							styles={customStyles}
							value={statusOptions.find((o) => o.value === validation.values.status) || statusOptions[0]}
							onChange={(opt) => validation.setFieldValue("status", opt.value)}
							options={statusOptions}
						/>
					</FormGroup>
				</Col>
			</>
		)}
		{id && !isModal && (
			<div
				className="container-fluid"
				style={{
					marginTop: 20,
					background: "#f3f3f9",
					borderRadius: 10,
					padding: 20,
				}}
			>
				<h4>Configurações do indicador</h4>
				<Configuration id={id} />
			</div>
		)}
		{isModal && (
			<VariableConfiguration
				ref={variableConfigRef}
				goalId={id}
				indicatorPeriodicity={validation.values.periodicity}
			/>
		)}
			{(id || isModal) && (
				<>
					<div
						className="container-fluid"
						style={{
							marginTop: 20,
							background: "#f3f3f9",
							borderRadius: 10,
							padding: 20,
						}}
					>
						<h4>Informações Opcionais</h4>
						<FormGroup className="mb-3">
							<Label>
								Insira informações adicionais sobre a meta
							</Label>
							<Input
								name="additional_informations"
								type="textarea"
								rows={10}
								style={{
									minHeight: "200px",
									resize: "vertical",
								}}
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={
									validation.values
										.additional_informations || ""
								}
								invalid={
									validation.touched
										.additional_informations &&
									!!validation.errors
										.additional_informations
								}
							/>
							{validation.touched.additional_informations &&
							validation.errors.additional_informations ? (
								<FormFeedback type="invalid">
									{
										validation.errors
											.additional_informations
									}
								</FormFeedback>
							) : null}
						</FormGroup>
					</div>
				</>
			)}
			</Row>
			<div
				className="page-title-right"
				style={{
					display: "flex",
					justifyContent: "end",
					marginTop: 20,
					gap: 10,
				}}
			>
				{id && temPermissaoCopiar && (
					<Button
						onClick={() => handleCopyIndicador()}
						style={buttonStyle}
					>
						<Copy size={16} /> Copiar
					</Button>
				)}
				<Button
					disabled={loading}
					type="submit"
					style={buttonStyle}
				>
				{loading ? (
					<span
						className="spinner-border spinner-border-sm mr-1"
						style={{ marginRight: 10 }}
					></span>
				) : null}
				{id || isModal ? (
					<>
						<FloppyDisk size={16} /> Salvar
					</>
				) : (
					"Avançar"
				)}
				</Button>
				{isModal && onClose && (
					<Button
						type="button"
						onClick={onClose}
						style={buttonStyle}
					>
						<SignOut size={16} /> Fechar
					</Button>
				)}
			</div>
		</Form>
	);

	if (isModal) {
		if (isLoading) {
			return (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						padding: "40px 0",
						gap: 8,
					}}
				>
					<span
						className="spinner-border spinner-border-sm"
						style={{ marginRight: 10 }}
					></span>
					Carregando...
				</div>
			);
		}

		return (
			<>
				{formJSX}
				<CopyIndicadorModal
					isOpen={isCopyModalOpen}
					toggle={() => setIsCopyModalOpen(false)}
					onCopy={handleConfirmCopy}
					indicadorData={data}
				/>
			</>
		);
	}

	return (
		<React.Fragment>
			<Loading isLoading={isLoading}>
				<div className="page-content">
					<Breadcrumbs
						title={t("Indicadores")}
						breadcrumbItem={t("Formulário")}
						backLink={getBackLink()}
					/>
					<div
						className="container-fluid"
						style={{
							backgroundColor: "white",
							padding: 20,
							borderRadius: 10,
							marginTop: 25,
						}}
					>
						{formJSX}
					</div>
				</div>
			</Loading>

			<CopyIndicadorModal
				isOpen={isCopyModalOpen}
				toggle={() => setIsCopyModalOpen(false)}
				onCopy={handleConfirmCopy}
				indicadorData={data}
			/>
		</React.Fragment>
	);
};

const FormIndicadores = (props) => {
	document.title = "Senda | Indicadores";

	const params = props.router.params;
	const id = params?.id;

	return <IndicadorFormContent key={id || "new"} id={id} isModal={false} t={props.t} />;
};

FormIndicadores.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withRouter(withTranslation()(FormIndicadores));
